import React from "react";
import { Info } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FormikInput } from "@/components/FormikField";
import type { FormikProps } from "formik";
import type { SilaeFormData } from "@/types/silae";
import { useTranslation } from "react-i18next";
import { useVariableNameCollisions } from "@/hooks/useVariableNameCollisions";
import { VariableNameCollisionAlert } from "./VariableNameCollisionAlert";

interface EvpForceConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0"
  className?: string;
}

/**
 * Composant réutilisable pour la configuration du forçage via EVP
 */
export const EvpForceConfig: React.FC<EvpForceConfigProps> = ({
  formik,
  fieldPrefix,
  className = "",
}) => {
  const { t } = useTranslation();

  // Helper pour accéder aux valeurs imbriquées
  const getFieldValue = (path: string) => {
    const keys = path.split(".");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let value: any = formik.values;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return undefined;
    }
    return value;
  };

  const enableField = `${fieldPrefix}.enableEvpForce`;
  const titleField = `${fieldPrefix}.evpForceTitle`;

  const isEnabled = getFieldValue(enableField) as boolean;
  const evpForceTitleValue = getFieldValue(titleField) as string;
  const collisions = useVariableNameCollisions(formik.values);

  return (
    <div className={`space-y-4 pt-4 border-t ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-sm font-medium">{t("evpForcing.title")}</Label>
          <p className="text-xs text-muted-foreground">
            {t("evpForcing.description")}
          </p>
        </div>
        <Switch
          checked={isEnabled || false}
          onCheckedChange={(checked) => {
            formik.setFieldValue(enableField, checked);
            if (!checked) {
              formik.setFieldValue(titleField, "");
            }
            setTimeout(() => formik.validateForm(), 0);
          }}
        />
      </div>

      {isEnabled && (
        <div className="space-y-2 pt-2">
          <FormikInput
            formik={formik}
            name={titleField}
            label={t("evpForcing.variableName")}
            placeholder={t("evpForcing.variablePlaceholder")}
            uppercase
          />
          <VariableNameCollisionAlert
            name={evpForceTitleValue}
            duplicates={collisions}
          />

          {/* Information sur le forçage EVP */}
          <div className="flex items-start gap-2 p-3 bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800 rounded-lg">
            <Info className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-sky-700 dark:text-sky-300 space-y-1">
              <p className="text-xs">{t("evpForcing.helpText2")}</p>
              <p className="text-xs">{t("evpForcing.helpText3")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
